// 🔧 settings_map.js
// ✅ Purpose: Admin-only Event Map Settings page
// ✅ Features:
// - Shows the uploaded event map from /events/{eventId}/settings/voting.eventMap
// - Lets managers click the map to place Event Location markers
// - Supports bathrooms, registration, first aid, awards, food, parking, gates, and custom locations
// - Stores event location markers in /events/{eventId}/settings/voting.eventLocationMarkers
// - Uses percentage-based X/Y coordinates so markers stay responsive
// - Shows marker preview directly on the map
// - Allows deleting event location markers
// - Shows success/error notifications for 20 seconds
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import { useRole } from "../../role_context";
import { useIsEventManager } from "../../hooks/use_is_event_manager";
import FullPageSpinner from "../../components/FullPageSpinner";
import { showStatus } from "../../utils/status_helpers";
import { logger } from "../../utils/logger";
import { Link } from "react-router-dom";

const SettingsMap = () => {
  const { eventslug, eventId, eventLoading, ready } = useEventContext({
    redirectIfNotFound: true,
  });

  const { role, eventRole } = useRole();
  const isManager = useIsEventManager(eventId);

  const [settingsLoading, setSettingsLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [eventMap, setEventMap] = useState("");
  const [eventLocationMarkers, setEventLocationMarkers] = useState([]);
  const [pendingLocationPoint, setPendingLocationPoint] = useState(null);
  const [saving, setSaving] = useState(false);
    const [sponsorMarkers, setSponsorMarkers] = useState([]);
    const [vendorMarkers, setVendorMarkers] = useState([]);
    const [showSponsorMarkers, setShowSponsorMarkers] = useState(true);
    const [showVendorMarkers, setShowVendorMarkers] = useState(true);
    const [showEventLocationMarkers, setShowEventLocationMarkers] = useState(true);
  const [locationMarkerForm, setLocationMarkerForm] = useState({
    markerLabel: "",
    title: "",
    note: "",
    locationType: "event_location",
  });

  const canAccess =
    ["admin", "manager"].includes(role) ||
    eventRole === "manager" ||
    isManager;

  const locationTypeOptions = [
    { value: "event_location", label: "Event Location", markerLabel: "E", className: "bg-warning text-dark" },
    { value: "restroom", label: "Bathroom / Restroom", markerLabel: "B", className: "bg-info text-dark" },
    { value: "registration", label: "Registration", markerLabel: "R", className: "bg-primary text-white" },
    { value: "first_aid", label: "First Aid", markerLabel: "+", className: "bg-danger text-white" },
    { value: "food", label: "Food Area", markerLabel: "F", className: "bg-secondary text-white" },
    { value: "awards", label: "Awards Stage", markerLabel: "A", className: "bg-dark text-white" },
    { value: "parking", label: "Parking", markerLabel: "P", className: "bg-success text-white" },
    { value: "gate", label: "Gate / Entrance", markerLabel: "G", className: "bg-light text-dark" },
  ];

  const selectedLocationType =
    locationTypeOptions.find((item) => item.value === locationMarkerForm.locationType) ||
    locationTypeOptions[0];

  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    const loadMapSettings = async () => {
      if (!eventId) return;

      try {
        logger.log("🗺 Loading map settings", { eventId });

        const snap = await getDoc(
          doc(db, "events", eventId, "settings", "voting")
        );

        const data = snap.exists() ? snap.data() : {};

        setEventMap(data.eventMap || "");
        setEventLocationMarkers(
          Array.isArray(data.eventLocationMarkers)
            ? data.eventLocationMarkers
            : []
        );
      } catch (err) {
        logger.error("❌ Failed to load map settings:", { value: err });

        showStatus(setStatus, {
          type: "error",
          message: "Failed to load map settings.",
        });
      } finally {
        setSettingsLoading(false);
      }
    };

    loadMapSettings();
  }, [eventId]);

  useEffect(() => {
    const loadLinkedMapMarkers = async () => {
        if (!eventId) return;

        try {
        logger.log("🗺 Loading sponsor/vendor map markers", { eventId });

        const sponsorsSnap = await getDocs(
            collection(db, "events", eventId, "sponsors")
        );

        const sponsorsWithMarkers = sponsorsSnap.docs
            .map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
            mapMarkerType: docSnap.data().mapMarkerType || "sponsor",
            }))
            .filter(
            (sponsor) =>
                sponsor.mapMarkerX !== null &&
                sponsor.mapMarkerX !== undefined &&
                sponsor.mapMarkerY !== null &&
                sponsor.mapMarkerY !== undefined
            );

        setSponsorMarkers(sponsorsWithMarkers);

        // 🛍️ Future-ready vendor support.
        // This will work when /events/{eventId}/vendors exists and vendor records
        // use the same mapMarkerX/mapMarkerY/mapMarkerType fields.
        try {
            const vendorsSnap = await getDocs(
            collection(db, "events", eventId, "vendors")
            );

            const vendorsWithMarkers = vendorsSnap.docs
            .map((docSnap) => ({
                id: docSnap.id,
                ...docSnap.data(),
                mapMarkerType: docSnap.data().mapMarkerType || "vendor",
            }))
            .filter(
                (vendor) =>
                vendor.mapMarkerX !== null &&
                vendor.mapMarkerX !== undefined &&
                vendor.mapMarkerY !== null &&
                vendor.mapMarkerY !== undefined
            );

            setVendorMarkers(vendorsWithMarkers);
        } catch (vendorErr) {
            logger.warn("⚠️ Vendor markers not loaded. Vendor collection may not exist yet.", {
            value: vendorErr,
            });

            setVendorMarkers([]);
        }
        } catch (err) {
        logger.error("❌ Failed to load sponsor/vendor map markers:", {
            value: err,
        });

        setSponsorMarkers([]);
        setVendorMarkers([]);
        }
    };

    loadLinkedMapMarkers();
    }, [eventId]);

  const handleEventLocationMapClick = (e) => {
    if (!eventMap) return;

    const rect = e.currentTarget.getBoundingClientRect();

    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setPendingLocationPoint({
      x: Number(x.toFixed(2)),
      y: Number(y.toFixed(2)),
    });

    showStatus(setStatus, {
      type: "info",
      message: "Map point selected. Add a title, then save the location marker.",
    });
  };

  const handleAddEventLocationMarker = async () => {
    if (!pendingLocationPoint) {
      showStatus(setStatus, {
        type: "warning",
        message: "Click the map to place this location first.",
      });

      return;
    }

    if (!String(locationMarkerForm.title || "").trim()) {
      showStatus(setStatus, {
        type: "warning",
        message: "Location title is required.",
      });

      return;
    }

    setSaving(true);

    try {
      const fallbackLabel =
        locationMarkerForm.markerLabel ||
        selectedLocationType.markerLabel ||
        "E";

      const newMarker = {
        id: crypto.randomUUID(),
        title: String(locationMarkerForm.title || "").trim(),
        note: String(locationMarkerForm.note || "").trim(),
        markerLabel: String(fallbackLabel || "E").trim(),
        mapMarkerType: "event_location",
        locationType: locationMarkerForm.locationType || "event_location",
        mapMarkerColor: selectedLocationType.className,
        mapMarkerX: pendingLocationPoint.x,
        mapMarkerY: pendingLocationPoint.y,
        createdAt: new Date().toISOString(),
      };

      const updatedMarkers = [...eventLocationMarkers, newMarker];

      await setDoc(
        doc(db, "events", eventId, "settings", "voting"),
        {
          eventLocationMarkers: updatedMarkers,
          lastSaved: Timestamp.now(),
        },
        { merge: true }
      );

      setEventLocationMarkers(updatedMarkers);
      setPendingLocationPoint(null);

      setLocationMarkerForm({
        markerLabel: "",
        title: "",
        note: "",
        locationType: "event_location",
      });

      showStatus(setStatus, {
        type: "success",
        message: "Event location marker added.",
      });
    } catch (err) {
      logger.error("❌ Failed to add event location marker:", { value: err });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to add event location marker.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEventLocationMarker = async (markerId) => {
    if (!window.confirm("Delete this event location marker?")) return;

    setSaving(true);

    try {
      const updatedMarkers = eventLocationMarkers.filter(
        (marker) => marker.id !== markerId
      );

      await setDoc(
        doc(db, "events", eventId, "settings", "voting"),
        {
          eventLocationMarkers: updatedMarkers,
          lastSaved: Timestamp.now(),
        },
        { merge: true }
      );

      setEventLocationMarkers(updatedMarkers);

      showStatus(setStatus, {
        type: "warning",
        message: "Event location marker removed.",
      });
    } catch (err) {
      logger.error("❌ Failed to delete event location marker:", { value: err });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to delete event location marker.",
      });
    } finally {
      setSaving(false);
    }
  };

  const markerToggleButtonClass = (active) =>
    active ? "btn btn-dark btn-sm" : "btn btn-outline-secondary btn-sm";

  if (!ready || eventLoading || settingsLoading || isManager === null) {
    return <FullPageSpinner message="Loading map settings..." />;
  }

  if (!canAccess) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          You do not have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1200px" }}>
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">🗺 Event Map</h1>

            <p className="lead text-muted mb-0">
              Add public map markers for bathrooms, registration, first aid,
              food areas, awards, parking, gates, and other event locations.
            </p>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        {!eventMap && (
          <div className="alert alert-warning">
            No event map image has been uploaded yet. Upload the map from
            Event Setup → Images before placing markers.
          </div>
        )}

        <div className="card shadow-sm border-0 mb-4">
          <div className="card-header bg-white">
            <strong>📍 Add Event Location Marker</strong>
          </div>

          <div className="card-body">
            <div className="row g-3 mb-4">
              <div className="col-12 col-md-3">
                <label className="form-label fw-bold">Location Type</label>

                <select
                  className="form-select"
                  value={locationMarkerForm.locationType}
                  onChange={(e) =>
                    setLocationMarkerForm((prev) => ({
                      ...prev,
                      locationType: e.target.value,
                      markerLabel:
                        locationTypeOptions.find((item) => item.value === e.target.value)
                          ?.markerLabel || prev.markerLabel,
                    }))
                  }
                >
                  {locationTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-2">
                <label className="form-label fw-bold">Marker Label</label>

                <input
                  className="form-control"
                  value={locationMarkerForm.markerLabel}
                  onChange={(e) =>
                    setLocationMarkerForm((prev) => ({
                      ...prev,
                      markerLabel: e.target.value,
                    }))
                  }
                  placeholder={selectedLocationType.markerLabel}
                />
              </div>

              <div className="col-12 col-md-7">
                <label className="form-label fw-bold">Location Title</label>

                <input
                  className="form-control"
                  value={locationMarkerForm.title}
                  onChange={(e) =>
                    setLocationMarkerForm((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                  placeholder="Bathrooms, Registration Tent, First Aid, etc."
                />
              </div>

              <div className="col-12">
                <label className="form-label fw-bold">Location Note</label>

                <textarea
                  className="form-control"
                  rows="3"
                  value={locationMarkerForm.note}
                  onChange={(e) =>
                    setLocationMarkerForm((prev) => ({
                      ...prev,
                      note: e.target.value,
                    }))
                  }
                  placeholder="Example: Near the registration tent."
                />
              </div>
            </div>

            {eventMap && (
              <>
                <div className="alert alert-info">
                  Click the map to place the marker. Then fill in the location
                  details and save.
                </div>

                <div
                  className="position-relative border rounded overflow-hidden bg-light mb-3"
                  onClick={handleEventLocationMapClick}
                  style={{ cursor: "crosshair" }}
                >
                  <img
                    src={eventMap}
                    alt="Event Map"
                    className="img-fluid d-block w-100"
                  />

                    {showSponsorMarkers &&
                    sponsorMarkers.map((marker) => (
                        <Link
                        key={`sponsor-${marker.id}`}
                        to={`/${eventslug}/sponsor/${marker.sponsorSlug || marker.id}`}
                        className="position-absolute translate-middle bg-success text-white rounded-circle d-flex align-items-center justify-content-center fw-bold shadow text-decoration-none"
                        style={{
                            left: `${marker.mapMarkerX}%`,
                            top: `${marker.mapMarkerY}%`,
                            width: "36px",
                            height: "36px",
                            border: "3px solid #fff",
                            zIndex: 5,
                        }}
                        title={`Edit sponsor: ${marker.name || marker.mapMarkerTitle || "Sponsor"}`}
                        onClick={(e) => e.stopPropagation()}
                        >
                        {marker.mapMarkerLabel || "S"}
                        </Link>
                    ))}

                    {showVendorMarkers &&
                    vendorMarkers.map((marker) => (
                        <Link
                        key={`vendor-${marker.id}`}
                        to={`/${eventslug}/vendor/${marker.vendorSlug || marker.id}`}
                        className="position-absolute translate-middle bg-primary text-white rounded-circle d-flex align-items-center justify-content-center fw-bold shadow text-decoration-none"
                        style={{
                            left: `${marker.mapMarkerX}%`,
                            top: `${marker.mapMarkerY}%`,
                            width: "36px",
                            height: "36px",
                            border: "3px solid #fff",
                            zIndex: 5,
                        }}
                        title={`Edit vendor: ${marker.name || marker.mapMarkerTitle || "Vendor"}`}
                        onClick={(e) => e.stopPropagation()}
                        >
                        {marker.mapMarkerLabel || "V"}
                        </Link>
                    ))}

                {showEventLocationMarkers &&
                eventLocationMarkers.map((marker) => (
                    <div
                    key={`event-location-${marker.id}`}
                    className={`position-absolute translate-middle ${marker.mapMarkerColor || "bg-warning text-dark"} rounded-circle d-flex align-items-center justify-content-center fw-bold shadow`}
                    style={{
                        left: `${marker.mapMarkerX}%`,
                        top: `${marker.mapMarkerY}%`,
                        width: "36px",
                        height: "36px",
                        border: "3px solid #fff",
                        pointerEvents: "none",
                    }}
                    title={marker.title}
                    >
                    {marker.markerLabel || "E"}
                    </div>
                ))}
                
                  {pendingLocationPoint && (
                    <div
                      className={`position-absolute translate-middle ${selectedLocationType.className} rounded-circle d-flex align-items-center justify-content-center fw-bold shadow`}
                      style={{
                        left: `${pendingLocationPoint.x}%`,
                        top: `${pendingLocationPoint.y}%`,
                        width: "42px",
                        height: "42px",
                        border: "3px solid #fff",
                        pointerEvents: "none",
                      }}
                    >
                      {locationMarkerForm.markerLabel || selectedLocationType.markerLabel}
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="d-flex flex-wrap justify-content-center gap-2 mb-4">
            <button
                type="button"
                className={markerToggleButtonClass(showSponsorMarkers)}
                onClick={() => setShowSponsorMarkers((prev) => !prev)}
            >
                🌟 Sponsors ({sponsorMarkers.length})
            </button>

            <button
                type="button"
                className={markerToggleButtonClass(showVendorMarkers)}
                onClick={() => setShowVendorMarkers((prev) => !prev)}
            >
                🛍️ Vendors ({vendorMarkers.length})
            </button>

            <button
                type="button"
                className={markerToggleButtonClass(showEventLocationMarkers)}
                onClick={() => setShowEventLocationMarkers((prev) => !prev)}
            >
                📍 Event Locations ({eventLocationMarkers.length})
            </button>
            </div>

            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAddEventLocationMarker}
              disabled={saving || !eventMap}
            >
              {saving ? "Saving..." : "➕ Add Event Location Marker"}
            </button>
          </div>
        </div>

        <div className="card shadow-sm border-0">
          <div className="card-header bg-white">
            <strong>📋 Event Location Markers</strong>
          </div>

          <div className="card-body">
            {eventLocationMarkers.length === 0 ? (
              <div className="alert alert-light border mb-0">
                No event location markers have been added yet.
              </div>
            ) : (
              <div className="list-group">
                {eventLocationMarkers.map((marker) => (
                  <div
                    key={marker.id}
                    className="list-group-item d-flex flex-column flex-md-row justify-content-between gap-3"
                  >
                    <div>
                      <div className="fw-bold">
                        {marker.markerLabel || "E"} — {marker.title}
                      </div>

                      {marker.note && (
                        <div className="small text-muted">
                          {marker.note}
                        </div>
                      )}

                      <div className="small text-muted">
                        X: {marker.mapMarkerX}% / Y: {marker.mapMarkerY}%
                      </div>
                    </div>

                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm align-self-start"
                      onClick={() => handleDeleteEventLocationMarker(marker.id)}
                      disabled={saving}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SettingsMap;
